// Fixed controls and limits that the server enforces and the web displays (plan 7.5, 7.6, 11.4, 11.9, 11.10, 11.11).
// Values that the policy screen may tune are given as {default, options|min|max}; everything else is fixed.
import { z } from 'zod';
import { REASON_CATEGORIES } from './enums.js';

/**
 * Content-view fixed control flow (plan 7.5.1). Cannot be switched off; only these values move, and only stricter:
 * reason length 10→15/20/30 (never below 10), re-auth validity 10→5 minutes (never above 10), grant 10–60 minutes.
 */
export const ACCESS_CONTROL = {
  reasonCategories: REASON_CATEGORIES,
  minReasonLength: { default: 10, options: [10, 15, 20, 30] },
  /** Max length of the reason text (mockup textarea maxlength). */
  reasonMaxLength: 200,
  reauthValidMinutes: { default: 10, options: [10, 5] },
  grantMinutes: { default: 60, min: 10, max: 60 },
  grantUnit: 'conversation',
  /** Google re-auth uses OIDC prompt=login with max_age = reauth minutes × 60 (600 or 300). */
  googleReauthMaxAgeSec: { 10: 600, 5: 300 },
  /** Response header for any text body (plan 7.5.1 step 5). */
  cacheControl: 'no-store',
} as const;

/** The tunable part of the fixed flow as stored in the policy (validated: only stricter than the defaults). */
export const AccessControlValues = z.object({
  minReasonLength: z.union([z.literal(10), z.literal(15), z.literal(20), z.literal(30)]),
  reauthValidMinutes: z.union([z.literal(10), z.literal(5)]),
  grantMinutes: z.int().min(ACCESS_CONTROL.grantMinutes.min).max(ACCESS_CONTROL.grantMinutes.max),
  /** Max days an "investigation notify deferral" may last (plan 7.5.3 — value is a policy decision, default 30). */
  notifyDeferralMaxDays: z.int().min(1).max(90),
  /** Real-time alert: N content views within one hour without an investigation case (plan 11.8.4, initial value open). */
  bulkViewAlertPerHour: z.int().min(2).max(100),
});
export type AccessControlValues = z.infer<typeof AccessControlValues>;
export const DEFAULT_ACCESS_CONTROL_VALUES: AccessControlValues = {
  minReasonLength: 10,
  reauthValidMinutes: 10,
  grantMinutes: 60,
  notifyDeferralMaxDays: 30,
  bulkViewAlertPerHour: 5,
};

/** Small-group protection (plan 7.6): fixed at 3; only raised via environment (never lowered on screen). */
export const SMALL_GROUP_MIN = 3;
/** Export cell for a hidden head count (plan 7.6, 11.10). */
export const SMALL_GROUP_EXPORT_MARK = '<3';
/** Time metrics need at least this many samples, else "too few samples" (plan 9.1.4, 10.1.4). */
export const MIN_LATENCY_SAMPLES = 5;
/** Over this many days p50/p95 are computed from histogram buckets and marked "approx." (plan 10.3.6). */
export const LATENCY_EXACT_MAX_DAYS = 31;

/** Login & session (plan 11.4.1, 11.4.2). */
export const AUTH_LIMITS = {
  lockAfterFailures: 5,
  lockMinutes: 15,
  ipLimit: { windowMinutes: 5, maxAttempts: 30 },
  password: { minLength: 10, minClasses: 3 },
  tempPasswordLength: 18,
  inviteValidDays: 7,
  session: { normal: { idleMinutes: 120, absoluteHours: 12 }, admin: { idleMinutes: 30, absoluteHours: 8 } },
  lastSeenUpdateSeconds: 60,
  totpDigits: 6,
} as const;

/** Cookie/header names for the web session (plan 11.4.2). */
export const SESSION_COOKIE = 'aiax_session';
export const CSRF_COOKIE = 'aiax_csrf';
export const CSRF_HEADER = 'x-csrf-token';
export const REQUEST_ID_HEADER = 'x-request-id';

/** Connection tokens (plan 11.11). */
export const TOKEN_LIMITS = {
  collectMaxDays: 90,
  gatewayMaxDays: 90,
  gatewayDayOptions: [30, 60, 90],
  activePerUser: 10,
  expiryNoticeDays: 14,
  keyRotationGraceDays: 7,
} as const;

/** Connection state thresholds (plan 8.4.8): ok ≤ 4 h, quiet 4 h – 3 d, down > 3 d; wizard waits 3 minutes. */
export const CONNECTION_THRESHOLDS = { quietAfterHours: 4, downAfterDays: 3, testWaitMinutes: 3 } as const;

/** Grants & reviews (plan 7.4.2). */
export const GRANT_RULES = { expiryNoticeDays: 14, dormantDays: 90, maxExpiryDays: 365 } as const;

/** Export controls (plan 11.10). */
export const EXPORT_LIMITS = { rowDefault: 10_000, rowMax: 50_000, formulaPrefixes: ['=', '+', '-', '@', '\t', '\r'] } as const;

/** Deletion & retention (plan 11.9). Days; null = no bound. */
export const DELETION_RULES = { autoDeleteAfterDays: 7, memoMinLength: 5 } as const;
export const RETENTION_TARGETS = [
  'content',
  'raw_gateway',
  'usage',
  'aggregate',
  'consent',
  'audit_permission',
  'audit_access',
  'audit_general',
  'backup',
] as const;
export const RetentionTarget = z.enum(RETENTION_TARGETS);
export type RetentionTarget = z.infer<typeof RetentionTarget>;
export const RETENTION_DEFAULTS: Record<RetentionTarget, { days: number | null; min: number | null; max: number | null; editable: boolean }> = {
  content: { days: 30, min: 7, max: 90, editable: true },
  raw_gateway: { days: 7, min: 1, max: 7, editable: true },
  usage: { days: 180, min: 90, max: 365, editable: true },
  aggregate: { days: 730, min: null, max: null, editable: false },
  consent: { days: null, min: null, max: null, editable: false },
  audit_permission: { days: 1095, min: 1095, max: null, editable: true },
  audit_access: { days: 730, min: 365, max: null, editable: true },
  audit_general: { days: 365, min: 365, max: null, editable: true },
  backup: { days: 30, min: null, max: 30, editable: false },
};

/** Collection gap alert (plan 8.2.2): whole-server intake at 0 for ≥ 30 minutes during work hours. */
export const COLLECTION_GAP_ALERT_MINUTES = 30;

/** Ingest limits shared with the collector (plan 4.6.4, 6.2.3). */
export const INGEST_LIMITS = { contentMaxChars: 65_536, futureSkewMinutes: 10 } as const;

/** Custom period rules (plan 10.3.1, mockup core.js validCustom): ≤ 366 days inclusive, no future date, from ≤ to. */
export const PERIOD_RULES = { customMaxDays: 366, hourBucketMaxDays: 7 } as const;

/** Paging defaults (mockup ui.pager.PAGE_SIZE = 15 for logs/people tables). */
export const PAGE_SIZE_DEFAULT = 15;
export const PAGE_SIZE_MAX = 100;

/** Undo window of applied admin changes (UndoTicket.until = now + this). The web counts down from `until`. */
export const UNDO_WINDOW_SECONDS = 10;

/** Display codes shown on screens (opaque ids never are): approvals, investigation cases, deletion requests. */
export const DISPLAY_CODE_PATTERNS = {
  approval: /^APR-\d{4,}$/,
  case: /^INV-\d{4}-\d{3,}$/,
  deletion: /^DR-\d{4,}$/,
} as const;

/**
 * Glossary edit input rules (plan 9.5.9, mockup glossary.js): plain text + light markdown only, https links only.
 */
export const GLOSSARY_RULES = {
  slugPattern: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
  slugMax: 60,
  shortMax: 200,
  longMax: 600,
  aliasesMax: 400,
  relatedMax: 300,
  urlMax: 400,
  /** Rejected in text fields: HTML tags or raw links (links belong in url_ko/url_en). */
  forbiddenText: /<[^>]*>|https?:\/\//i,
  httpsUrl: /^https:\/\/[^\s/]+\.[^\s]+$/,
} as const;
